import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 350;
const MARGIN = { top: 20, right: 30, bottom: 60, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },
  summaryCard: {
    background: '#f9fafb',
    padding: '12px',
    borderRadius: '6px',
  },
  summaryLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: '4px',
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: 700,
  },
  summaryNote: {
    fontSize: '10px',
    color: '#9ca3af',
    marginTop: '2px',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function ValueAtRiskChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const n = data.dates.length;
    const changes = data.changes || [];
    const var95 = data.var95 || [];
    const var99 = data.var99 || [];
    const cvar95 = data.cvar95 || [];
    const breaches95 = data.breaches95 || [];

    // Collect all values for Y domain
    const allValues = [];
    changes.forEach((v) => { if (v != null) allValues.push(v); });
    var95.forEach((v) => { if (v != null) allValues.push(v); });
    var99.forEach((v) => { if (v != null) allValues.push(v); });
    cvar95.forEach((v) => { if (v != null) allValues.push(v); });

    const yMin = d3.min(allValues) || -2;
    const yMax = d3.max(allValues) || 2;
    const yPadding = Math.max((yMax - yMin) * 0.15, 0.3);

    // Scales
    const x = d3.scalePoint()
      .domain(data.dates)
      .range([0, innerW])
      .padding(0.5);

    const y = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', y(0))
      .attr('y2', y(0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '6,4');

    // CVaR shaded region (Expected Shortfall below VaR95)
    if (cvar95.length > 0 && var95.length > 0) {
      const cvarAreaData = data.dates.map((date, i) => ({
        date,
        cvar: cvar95[i],
        var: var95[i],
      })).filter((d) => d.cvar != null && d.var != null);

      if (cvarAreaData.length > 1) {
        const areaGen = d3.area()
          .x((d) => x(d.date))
          .y0((d) => y(d.var))
          .y1((d) => y(d.cvar))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(cvarAreaData)
          .attr('d', areaGen)
          .attr('fill', '#dc2626')
          .attr('fill-opacity', 0.08)
          .attr('stroke', 'none');
      }
    }

    // Bar width calculation
    const barWidth = n > 1
      ? Math.max(2, Math.min(20, (innerW / n) * 0.6))
      : 20;

    // Price change bars
    data.dates.forEach((date, i) => {
      const change = changes[i];
      if (change == null) return;

      const cx = x(date);
      const isGain = change >= 0;
      const barColor = isGain ? '#16a34a' : '#dc2626';
      const isBreach = breaches95[i] === true;

      const barY = isGain ? y(change) : y(0);
      const barH = Math.max(1, Math.abs(y(0) - y(change)));

      g.append('rect')
        .attr('x', cx - barWidth / 2)
        .attr('y', barY)
        .attr('width', barWidth)
        .attr('height', barH)
        .attr('fill', barColor)
        .attr('fill-opacity', isBreach ? 1 : 0.7)
        .attr('rx', 1);

      // Breach marker: diamond symbol above/below bar
      if (isBreach) {
        const markerY = isGain ? y(change) - 10 : y(change) + 10;
        g.append('path')
          .attr('d', d3.symbol().type(d3.symbolDiamond).size(60)())
          .attr('transform', `translate(${cx}, ${markerY})`)
          .attr('fill', '#f59e0b')
          .attr('stroke', '#92400e')
          .attr('stroke-width', 1);
      }
    });

    // VaR 95% line (blue dashed)
    if (var95.length > 0) {
      const var95Data = data.dates.map((date, i) => ({
        date,
        value: var95[i],
      })).filter((d) => d.value != null);

      const lineGen95 = d3.line()
        .x((d) => x(d.date))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(var95Data)
        .attr('d', lineGen95)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,4');
    }

    // VaR 99% line (red dashed)
    if (var99.length > 0) {
      const var99Data = data.dates.map((date, i) => ({
        date,
        value: var99[i],
      })).filter((d) => d.value != null);

      const lineGen99 = d3.line()
        .x((d) => x(d.date))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(var99Data)
        .attr('d', lineGen99)
        .attr('fill', 'none')
        .attr('stroke', '#dc2626')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,4');
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(formatDateLabel))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => {
            const sign = v > 0 ? '+' : '';
            return `${sign}$${v.toFixed(2)}`;
          })
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Invisible overlay columns for tooltip hover
    const colWidth = n > 1 ? innerW / n : innerW;

    data.dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            change: changes[i],
            var95Val: var95[i],
            var99Val: var99[i],
            cvar95Val: cvar95[i],
            isBreach: breaches95[i] === true,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Value at Risk (VaR)</div>
          <div style={styles.subtitle}>Maximum expected price change at confidence levels</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No VaR data available
        </div>
      </div>
    );
  }

  const summary = data.summary || {};
  const fieldLabel = data.field || 'pest365';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Value at Risk (VaR)</div>
        <div style={styles.subtitle}>
          Maximum expected price change at confidence levels &middot; {fieldLabel}
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="2" width="20" height="6" fill="#16a34a" fillOpacity="0.7" rx="1" />
          </svg>
          <span>Gain</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="2" width="20" height="6" fill="#dc2626" fillOpacity="0.7" rx="1" />
          </svg>
          <span>Loss</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" strokeDasharray="4,2" />
          </svg>
          <span>VaR 95%</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#dc2626" strokeWidth="2" strokeDasharray="4,2" />
          </svg>
          <span>VaR 99%</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#dc2626" fillOpacity="0.08" rx="2" />
          </svg>
          <span>CVaR (Expected Shortfall)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="14" height="14">
            <path
              d="M7,1 L12,7 L7,13 L2,7 Z"
              fill="#f59e0b"
              stroke="#92400e"
              strokeWidth="1"
            />
          </svg>
          <span>VaR Breach</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Summary stats panel */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Current VaR (95%)</div>
          <div style={{ ...styles.summaryValue, color: '#2563eb' }}>
            {summary.currentVar95 != null ? `$${summary.currentVar95.toFixed(2)}` : '--'}
          </div>
          <div style={styles.summaryNote}>Max expected daily loss</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Current VaR (99%)</div>
          <div style={{ ...styles.summaryValue, color: '#dc2626' }}>
            {summary.currentVar99 != null ? `$${summary.currentVar99.toFixed(2)}` : '--'}
          </div>
          <div style={styles.summaryNote}>Extreme loss threshold</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Breach Rate (95%)</div>
          <div style={{
            ...styles.summaryValue,
            color: summary.breachRate95 != null && summary.breachRate95 > 0.07 ? '#dc2626' : '#16a34a',
          }}>
            {summary.breachRate95 != null ? `${(summary.breachRate95 * 100).toFixed(1)}%` : '--'}
          </div>
          <div style={styles.summaryNote}>Expected ~5%</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Breach Rate (99%)</div>
          <div style={{
            ...styles.summaryValue,
            color: summary.breachRate99 != null && summary.breachRate99 > 0.02 ? '#dc2626' : '#16a34a',
          }}>
            {summary.breachRate99 != null ? `${(summary.breachRate99 * 100).toFixed(1)}%` : '--'}
          </div>
          <div style={styles.summaryNote}>Expected ~1%</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Max Loss</div>
          <div style={{ ...styles.summaryValue, color: '#dc2626' }}>
            {summary.maxLoss != null ? `$${summary.maxLoss.toFixed(2)}` : '--'}
          </div>
          <div style={styles.summaryNote}>Worst observed change</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Avg Loss</div>
          <div style={{ ...styles.summaryValue, color: '#f59e0b' }}>
            {summary.avgLoss != null ? `$${summary.avgLoss.toFixed(2)}` : '--'}
          </div>
          <div style={styles.summaryNote}>Mean negative change</div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Actual Change</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.change != null && tooltip.change >= 0 ? '#16a34a' : '#dc2626',
            }}>
              {tooltip.change != null
                ? `${tooltip.change >= 0 ? '+' : ''}$${tooltip.change.toFixed(2)}`
                : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>VaR 95%</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.var95Val != null ? `$${tooltip.var95Val.toFixed(2)}` : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#dc2626' }}>VaR 99%</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.var99Val != null ? `$${tooltip.var99Val.toFixed(2)}` : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>CVaR 95%</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.cvar95Val != null ? `$${tooltip.cvar95Val.toFixed(2)}` : '--'}
            </span>
          </div>
          {tooltip.isBreach && (
            <div style={{
              marginTop: '4px',
              paddingTop: '4px',
              borderTop: '1px solid #e5e7eb',
              color: '#f59e0b',
              fontWeight: 600,
              fontSize: '11px',
            }}>
              VaR Breach Detected
            </div>
          )}
        </div>
      )}
    </div>
  );
}
